import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/20/solid";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CandidateAction } from "./RoleCandidates";
import { Candidate, Questions, RoleStatusCandidateCount } from "../../../Models";
import { Switch } from "@headlessui/react";
import { ArrowPathIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useParams } from "react-router-dom";
import ExportCSVModal from "../../Shared/Modals/ExportCSVModal";
import { exportToCSV, exportToCSVWithSkills } from "../../../helpers/csvExport";
import { refreshRoleCandidateCache } from "../../../api/roles.api";
import useApiToken from "../../../hooks/useApiToken";
type Headers = {
  name: string;
  asc: boolean;
  fieldName: string;
  sort: boolean;
  isActive: boolean;
  isShipping: number;
};

const initialHeading: Headers[] = [
  {
    name: "Name",
    fieldName: "name",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },
  { name: "CV Errors", fieldName: "", asc: true, sort: false, isActive: false, isShipping: -1 },
  {
    name: "Location",
    fieldName: "workLocation1",
    asc: true,
    sort: false,
    isActive: false,
    isShipping: -1,
  },

  // {
  //   name: "Dept",
  //   fieldName: "shippingDepartment",
  //   asc: true,
  //   sort: false,
  //   isActive: false,
  //   isShipping: 1,
  // },
  // {
  //   name: "Top Rank",
  //   fieldName: "topRank",
  //   asc: true,
  //   sort: false,
  //   isActive: false,
  //   isShipping: 1,
  // },
  {
    name: "Age",
    fieldName: "age",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },
  // { name: "EG", fieldName: "EG", asc: true, sort: true, isActive: false },
  // { name: "WG", fieldName: "WG", asc: true, sort: true, isActive: false },
  // {
  //   name: "Exp",
  //   fieldName: "experienceInMonths",
  //   asc: true,
  //   sort: true,
  //   isActive: false,
  //   isShipping: -1,
  // },
  // {
  //   name: "OnBoardExp",
  //   fieldName: "onBoardExperienceInMonths",
  //   asc: true,
  //   sort: true,
  //   isActive: false,
  //   isShipping: 1,
  // },
  {
    name: "CvGrade",
    fieldName: "combinedGrade",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },
  {
    name: "CV Score",
    fieldName: "cvScore",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },
  // {
  //   name: "Gap",
  //   fieldName: "totalGapMonths",
  //   asc: true,
  //   sort: true,
  //   isActive: false,
  // },

  // {
  //   name: "graph",
  //   fieldName: "graph",
  //   asc: false,
  //   sort: false,
  //   isActive: false,
  // },

  {
    name: "Score",
    fieldName: "score",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },
  {
    name: "Grades",
    fieldName: "",
    asc: true,
    sort: false,
    isActive: false,
    // Shown for shipping too: shipping roles have no per-assessment score columns, so this is
    // where their assessment results appear.
    isShipping: -1,
  },
  // {
  //   name: "Assignment",
  //   fieldName: "",
  //   asc: true,
  //   sort: false,
  //   isActive: false,
  // },
  // {
  //   name: "YOE/Grad Date",
  //   fieldName: "yearsOfExperience",
  //   asc: true,
  //   sort: true,
  //   isActive: false,
  // },
  // {
  //   name: "Last University/Company",
  //   fieldName: "",
  //   asc: true,
  //   sort: false,
  //   isActive: false,
  // },
  // {
  //   name: "Time/Period",
  //   fieldName: "",
  //   asc: true,
  //   sort: false,
  //   isActive: false,
  // },
  {
    name: "Notice",
    fieldName: "noticePeriod",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: 0,
  },
  // Registered here so it takes part in sort state, saved sorts and the orderBy query like every
  // other column - but it is NOT drawn by the generic header loop: it renders in its own <th>
  // (see QGRADE_HEADER below) and only on roles that have answer weights. Must stay last so the
  // index-based slicing of initialHeading elsewhere is unaffected.
  {
    name: "QGrade",
    fieldName: "applicationScore",
    asc: true,
    sort: true,
    isActive: false,
    isShipping: -1,
  },

  // {
  //   name: "Date",
  //   fieldName: "latestStatusDate",
  //   asc: true,
  //   sort: true,
  //   isActive: false,
  // },
];

// Name of the QGrade entry in initialHeading - looked up by name so it can't drift.
const QGRADE_HEADER = "QGrade";

const SORT_STORAGE_KEY = 'candidatesSortState';

const saveSortToStorage = (sortData: any, roleId: string | undefined) => {
  try {
    const key = roleId ? `${SORT_STORAGE_KEY}_${roleId}` : SORT_STORAGE_KEY;
    const dataToStore = {
      ...sortData,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(key, JSON.stringify(dataToStore));
  } catch (error) {
    console.error('Failed to save sort state to localStorage:', error);
  }
};

const loadSortFromStorage = (roleId: string | undefined) => {
  try {
    const key = roleId ? `${SORT_STORAGE_KEY}_${roleId}` : SORT_STORAGE_KEY;
    const storedData = localStorage.getItem(key);
    if (storedData) {
      const parsed = JSON.parse(storedData);
      if (parsed?.sort) {
        const validFieldNames = new Set(
          initialHeading.map((h) => h.fieldName).filter((f) => f !== "")
        );
        parsed.sort = parsed.sort.filter(
          (s: { fieldName: string }) =>
            s.fieldName &&
            (validFieldNames.has(s.fieldName) ||
              s.fieldName.toLowerCase().startsWith("assessmentscore_"))
        );
      }
      return parsed;
    }
  } catch (error) {
    console.error('Failed to load sort state from localStorage:', error);
  }
  return null;
};

const clearSortFromStorage = (roleId: string | undefined) => {
  try {
    const key = roleId ? `${SORT_STORAGE_KEY}_${roleId}` : SORT_STORAGE_KEY;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear sort state from localStorage:', error);
  }
};

type props = {
  selectedCandidate: CandidateAction[];
  candidates: Candidate[];
  actionButtonsDisabled: boolean[];
  handleSelectedCandidates: (candidates: CandidateAction[]) => void;
  handleSort: (query: string) => void;
  handleBulkActionModal: (open: boolean) => void;
  handleBulkActionIsDisqualify: (open: boolean) => void;
  handleMediaAnswerDownload: () => void;
  handleAddAudioQuestion: () => void;
  handleCustomEmailModal: (open: boolean) => void;
  handleFlagCV: () => void;
  selectedColoumns: string[];
  setcolomns: React.Dispatch<React.SetStateAction<number>>;
  colomns: number;
  setagebased: React.Dispatch<React.SetStateAction<boolean>>;
  agebased: boolean;
  setskillcol: React.Dispatch<React.SetStateAction<number>>;
  skillcol: number;
  uniqueSkills: string[];
  roleListQuestions: Questions[];
  showRoleQGrading?: boolean;
  status: RoleStatusCandidateCount;
  isShipping: boolean;
  getAllCandidates: boolean;
  handleGetAllCandidates: (query: boolean) => void;
  takeAllCandidates: boolean;
  setTakeAllCandidates: React.Dispatch<React.SetStateAction<boolean>>;
  filterChanged: boolean;
  setFilterChanged: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function RoleCandidatesHeader({
  selectedCandidate,
  candidates,
  actionButtonsDisabled,
  handleSelectedCandidates,
  handleSort,
  handleBulkActionModal,
  handleBulkActionIsDisqualify,
  handleMediaAnswerDownload,
  handleAddAudioQuestion,
  handleCustomEmailModal,
  handleFlagCV,
  selectedColoumns,
  setcolomns,
  colomns,
  setagebased,
  agebased,
  setskillcol,
  skillcol,
  uniqueSkills,
  isShipping,
  roleListQuestions,
  showRoleQGrading,
  status,
  getAllCandidates,
  handleGetAllCandidates,
  takeAllCandidates,
  setTakeAllCandidates,
  filterChanged,
  setFilterChanged
}: props) {
  // const initialHeading = isShipping ? _initialHeading.filter(h => h.isShipping !== -1 || h.fieldName == "name") : _initialHeading;
  const [sort, setSort] = useState<Headers[]>([]);
  const [sortOptions, setSortOptions] = useState(initialHeading);
  const [checked, setChecked] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);
  const checkbox = useRef<HTMLInputElement>(null);
  const { roleId } = useParams();
  const token = useApiToken();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const isInitialMount = useRef(true);

  const additionalcolomns = isShipping
    ? ["EduGrade", "MariEduGrade", "WorkGrade", "OnBoardWorkGrade"]
    : ["EduGrade", "WorkGrade", "Gap", "Graph"];

  // One sortable header per assessment pipeline stage (Assessment 1, Assessment 2, ...),
  // matching the assessment score cells rendered in RoleCandidatesList. Sorting is handled
  // server-side via the `assessmentScore_<roleStatusId>` field (see RolesController.ApplyCandidateSort).
  const assessmentHeadings: Headers[] = useMemo(
    () =>
      status.roleStatuses
        .filter((rs) => rs.assessmentTemplateId || rs.assessmentId)
        .sort((a, b) => a.order - b.order)
        .map((rs) => ({
          name: rs.name,
          fieldName: `assessmentScore_${rs.id}`,
          asc: true,
          sort: true,
          isActive: false,
          isShipping: -1,
        })),
    [status.roleStatuses]
  );

  useEffect(() => {
    const storedSortData = loadSortFromStorage(roleId);
    if (storedSortData) {
      if (storedSortData.sort) {
        setSort(storedSortData.sort);
      }
    }
  }, [roleId]);

  useEffect(() => {
    if (sort.length > 0) {
      setSortOptions(prev => prev.map(option => {
        const activeSort = sort.find(s => s.name === option.name);
        if (activeSort) {
          return {
            ...option,
            isActive: true,
            asc: activeSort.asc
          };
        }
        return {
          ...option,
          isActive: false
        };
      }));
    }
  }, [sort]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const sortData = {
      sort,
      sortOptions
    };
    saveSortToStorage(sortData, roleId);
  }, [sort, sortOptions, roleId]);

  useEffect(() => {
    let sortQueryList: string[] = [];
    sort.forEach((s) => {
      if (s.fieldName === "name") {
        sortQueryList.push(`firstName${s.asc ? "" : " desc"}`);
        sortQueryList.push(`lastName${s.asc ? "" : " desc"}`);
      } else sortQueryList.push(`${s.fieldName}${s.asc ? "" : " desc"}`);
    });
    handleSort(sortQueryList.join(","));
  }, [sort, handleSort]);
  useEffect(() => {
    let selectedAdditionalColumns: string[] = [];
    if (colomns === 1) {
      selectedAdditionalColumns = additionalcolomns;
    }

    const additionalHeadings: Headers[] = selectedAdditionalColumns.map(
      (col) => {
        let fieldName = "";
        var isShippingField = 0;
        // Assign the correct field name based on the column
        if (col === "EduGrade") {
          fieldName = "totalEducationGrade";
          isShippingField = -1;
        } else if (col === "MariEduGrade") {
          fieldName = "totalMaritimeAcademyEducationGrade";
          isShippingField = 1;
        } else if (col === "WorkGrade") {
          fieldName = "totalExperienceGrade";
          isShippingField = -1;
        } else if (col === "OnBoardWorkGrade") {
          fieldName = "totalOnBoardExperienceGrade";
          isShippingField = 1;
        }

        return {
          name: col,
          fieldName: fieldName,
          asc: true,
          sort: true,
          isActive: false,
          isShipping: isShippingField,
        };
      }
    );

    // Integrate additionalHeadings into initialHeading
    const updatedHeading = [
      ...initialHeading.slice(0, 8), // Include the first 7 headings
      ...additionalHeadings, // Add the additional columns based on `columns`
      ...initialHeading.slice(8), // Include the remaining headings
    ];

    setSortOptions(updatedHeading);
  }, [colomns]);

  const handleOnCVG = () => {
    if (colomns === 1) {
      setcolomns(0);
    } else {
      setcolomns(1);
    }
  };

  const handleOnCVError = () => {
    if (colomns === 1 || colomns === 2) {
      setcolomns(0);

    } else {
      setcolomns(2);

    }

  };

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedCandidate.length > 0 &&
      selectedCandidate.length !== candidates.length;
    setChecked(selectedCandidate.length === candidates.length);
    setIndeterminate(isIndeterminate);
    if (checkbox.current) {
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedCandidate, candidates]);

  useEffect(() => {
    let additionalSkillHeadings: Headers[] = [];
    let additionalColumnHeadings: Headers[] = [];
    let educationPeriodFields: Headers[] = [];

    if (skillcol === 1) {
      additionalSkillHeadings = uniqueSkills.map((col) => ({
        name: col,
        fieldName: col,
        asc: true,
        sort: false,
        isActive: false,
        isShipping: -1,
      }));
    }

    if (colomns === 2) {
      educationPeriodFields = [
        { name: "Edu Error", fieldName: "", asc: true, sort: false, isActive: false, isShipping: -1 },
        { name: "Edu Period", fieldName: "", asc: true, sort: false, isActive: false, isShipping: -1 },
        { name: "Exp Error", fieldName: "", asc: true, sort: false, isActive: false, isShipping: -1 },
        { name: "Exp Period", fieldName: "", asc: true, sort: false, isActive: false, isShipping: -1 },
      ];
    }



    if (colomns === 1) {
      const selectedAdditionalColumns = isShipping
        ? ["EduGrade", "MariEduGrade", "WorkGrade", "OnBoardWorkGrade"]
        : ["EduGrade", "WorkGrade", "Gap", "Graph"];
      additionalColumnHeadings = selectedAdditionalColumns.map((col) => {
        let fieldName = "";
        if (col === "EduGrade") fieldName = "totalEducationGrade";
        else if (col === "WorkGrade") fieldName = "totalExperienceGrade";
        else if (col === "Gap") fieldName = "totalGapMonths";
        else if (col === "Graph") fieldName = "";

        let isShippingField = 0;
        if (col === "EduGrade") {
          fieldName = "totalEducationGrade";
          isShippingField = -1;
        } else if (col === "MariEduGrade") {
          fieldName = "totalMaritimeAcademyEducationGrade";
          isShippingField = 1;
        } else if (col === "WorkGrade") {
          fieldName = "totalExperienceGrade";
          isShippingField = -1;
        } else if (col === "OnBoardWorkGrade") {
          fieldName = "totalOnBoardExperienceGrade";
          isShippingField = 1;
        } else if (col === "Gap") {
          fieldName = "totalGapMonths";
          isShippingField = 0;
        } else if (col === "Graph") {
          fieldName = "";
          isShippingField = 0;
        }

        return {
          name: col,
          fieldName: fieldName,
          asc: true,
          sort: col === "Graph" ? false : true,
          isActive: false,
          isShipping: isShippingField,
        };
      });
    }

    const scoreIndex = initialHeading.findIndex(header => header.name === 'Score');
    const cvGradeIndex = initialHeading.findIndex(header => header.name === 'CvGrade');
    const cvErrorIndex = initialHeading.findIndex(header => header.name === 'CV Errors');
    const cvScoreIndex = initialHeading.findIndex(header => header.name === 'CV Score');

    // one sortable column per pipeline stage that has an assessment attached, replacing
    // the single blended "Score" column - a candidate can take a different test at each
    // status, so status (not the underlying assessment mapping) is the column identity.
    const assessmentScoreHeadings: Headers[] = (status.roleStatuses ?? [])
      .filter((rs) => rs.assessmentTemplateId || rs.assessmentId)
      .sort((a, b) => a.order - b.order)
      .map((rs) => ({
        name: rs.name,
        fieldName: `assessmentScore_${rs.id}`,
        asc: true,
        sort: true,
        isActive: false,
        // Non-shipping only. Shipping roles show assessment results in the Grades column
        // instead, and RoleCandidatesList never renders these cells for them - showing the
        // header there left empty, misaligned columns.
        isShipping: -1,
      }));

    let updatedHeading = [
      ...initialHeading.slice(0, cvErrorIndex + 1),  // Changed from cvErrorIndex + 0 to cvErrorIndex + 1
      ...educationPeriodFields,
      ...initialHeading.slice(cvErrorIndex + 1, cvGradeIndex + 1),  // Changed from cvErrorIndex + 0 to cvErrorIndex + 1
      ...additionalColumnHeadings,
      ...initialHeading.slice(cvGradeIndex + 1, cvScoreIndex + 1),
      // Assessment score columns sit right after CV Score in RoleCandidatesList's row markup.
      ...assessmentHeadings,
      // Stops BEFORE "Score": the per-assessment columns above replace the single blended Score
      // column, and RoleCandidatesList has no Score cell (it's commented out there). Including it
      // (scoreIndex + 1) put an extra header with no cells under it, shifting every later column
      // one place - Grades under "Score", Notice under "Grades", QGrade under "Notice".
      ...initialHeading.slice(cvScoreIndex + 1, scoreIndex),
      ...additionalSkillHeadings,
      ...initialHeading.slice(scoreIndex + 1),
    ];

    updatedHeading = updatedHeading.map((u) => {
      const applied = sort.find((s) => s.name === u.name);
      if (applied) return applied;
      else return u;
    });

    setSortOptions(updatedHeading);
  }, [skillcol, colomns, assessmentHeadings]);


  const handleSortChange = (sortOption: Headers) => {
    // Always preserve existing sort columns — only toggle direction or remove if already active
    let updatedSort = [...sort];
    const updatedSortedoption = sortOptions.map((so) => {
      if (so.isActive && so.name === sortOption.name) {
        if (so.asc) {
          // asc → desc
          let changedSort = updatedSort.find((us) => us.name === so.name);
          if (changedSort) changedSort.asc = false;
          return { ...so, asc: false };
        } else {
          // desc → remove
          const ind = updatedSort.findIndex((us) => us.name === so.name);
          if (ind !== -1) {
            updatedSort.splice(ind, 1);
          }
          return { ...so, asc: true, isActive: false };
        }
      } else if (!so.isActive && so.name === sortOption.name) {
        // New column: append to existing sort array
        updatedSort = [...updatedSort, { ...so, isActive: true }];
        return { ...so, isActive: true };
      } else {
        // When clicking a NEW column, deactivate all others; when toggling active, leave them
        return sortOption.isActive ? so : { ...so, isActive: false, asc: true };
      }
    });

    setSortOptions(updatedSortedoption);
    setSort(updatedSort);
  };

  function toggleAll() {
    handleSelectedCandidates(checked || indeterminate ? [] : candidates);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const handleOnSkill = () => {
    if (skillcol == 1) {
      setskillcol(0);
    }
    else {
      setskillcol(1);
    }
  }

  useEffect(() => {
    if (takeAllCandidates && candidates.length > 0 && !filterChanged) {
      handleSelectedCandidates(candidates);
      setTakeAllCandidates(false);
      setChecked(true);
    }
  }, [takeAllCandidates, candidates]);

  useEffect(() => {
    if (filterChanged) {
      setChecked(false);
      setFilterChanged(false);
      handleSelectedCandidates([]);
    }
  }, [filterChanged]);

  const handleExportCSV = (selectedColumns: string[], includeSkills: boolean) => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `candidates_${timestamp}.csv`;

    if (includeSkills) {
      exportToCSVWithSkills(candidates, selectedColumns, includeSkills, filename);
    } else {
      exportToCSV(candidates, selectedColumns, filename);
    }
  };

  const stripHtml = (html: string) =>
    html
      ? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()
      : "";

  return (
    <>
      <ExportCSVModal
        open={exportModalOpen}
        setOpen={setExportModalOpen}
        onExport={handleExportCSV}
        isShipping={isShipping}
      />
      <thead className="sticky top-16 bg-white dark:bg-darkbglight z-20 before:border-b before:border-gray-300 before:absolute before:content-[''] before:w-full before:h-full before:-z-10 dark:before:border-gray-500 dark:before:border-t">
        <tr className="">
          <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
            <input
              type="checkbox"
              className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-gray-100"
              ref={checkbox}
              checked={checked}
              onChange={toggleAll}
            />
          </th>

          {sortOptions.map((s, i) =>
            // QGrade has its own <th> below; skipped here but kept in sortOptions for sorting.
            s.name === QGRADE_HEADER ? null :
            s.name === "CvGrade" || s.name === "Score" ? (
              isShipping ? null
                :
                <th
                  key={s.name}
                  scope="col"
                  className={` cursor-pointer py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-300 w-fit min-w-14`}
                >
                  <div className="flex  items-center">
                    {s.name === "CvGrade" ? (
                      <div className={`flex items-center`}>
                        {/* <Switch
                      checked={agebased}
                      onChange={() => setagebased(!agebased)}
                      className={`group relative mr-2 flex items-center h-4 w-8 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                        agebased ? "bg-blue-600" : "bg-gray-200"
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-3 w-3 transform rounded-full shadow-lg transition duration-200 ease-in-out ${
                          agebased
                            ? "translate-x-4 bg-blue-800"
                            : "translate-x-0.5 bg-gray-700"
                        }`}
                      />
 
                      <span
                        className={`absolute text-xs font-bold ${
                          agebased
                            ? "text-white left-2"
                            : "text-gray-700 right-2"
                        }`}
                      >
                        {agebased ? "A" : "E"}
                      </span>
                    </Switch> */}
                      </div>
                    ) : (
                      <p></p>
                    )}
                    {/* <span onClick={handleOnCVG}>{s.name}</span> */}
                    <span
                      onClick={s.name === "CvGrade" ? handleOnCVG : handleOnSkill}
                      className="cursor-pointer whitespace-nowrap"
                    >
                      {s.name}
                    </span>
                    {s.sort && (
                      <span
                        className={`cursor-pointer hover:bg-gray-200 rounded-full dark:hover:bg-entntorange py-0.5 px-1.5 flex items-center ${s.isActive && "bg-blue-100 dark:bg-entntblue"
                          }`}
                        onClick={() => {
                          handleSortChange(s);
                        }}
                      >
                        <span
                          className={
                            s.isActive ? "text-entntblue dark:text-gray-300" : ""
                          }
                        >
                          {s.asc ? (
                            <ArrowUpIcon height={15} />
                          ) : (
                            <ArrowDownIcon height={15} />
                          )}
                        </span>
                        <span
                          className={`${s.isActive && "text-entntblue dark:text-gray-300"
                            } text-xs font-medium`}
                        >
                          {s.isActive &&
                            sort.findIndex((st) => st.name === s.name) + 1}
                        </span>
                      </span>
                    )}
                  </div>
                </th>
            ) :
              s.name === "CV Errors" ? (
                isShipping ? null
                  :
                  <th
                    key={s.name}
                    scope="col"
                    className={` cursor-pointer py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-300 w-fit min-w-14`}
                  >
                    <div className="flex  items-center">
                      <span
                        onClick={handleOnCVError}
                        className="cursor-pointer whitespace-nowrap"
                      >
                        {s.name}
                      </span>
                    </div>
                  </th>
              ) :
                (
                  (s.isShipping == -1 || s.isShipping == (isShipping ? 1 : 0)) && <th
                    key={s.name}
                    scope="col"
                    className={`py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-300 w-fit min-w-14`}
                  >
                    {selectedCandidate.length > 0 && (
                      <div className="absolute top-0 flex h-10 items-center space-x-3 sm:left-12 z-30 bg-white dark:bg-darkbglight">
                        {!getAllCandidates && <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={() => {
                            handleGetAllCandidates(true);

                          }}
                          disabled={
                            !selectedCandidate ||
                            selectedCandidate.length === 0 ||
                            actionButtonsDisabled[0]
                          }
                        >
                          Select All Candidates
                        </button>}
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={() => {
                            handleBulkActionIsDisqualify(false);
                            handleBulkActionModal(true);
                          }}
                          disabled={
                            !selectedCandidate ||
                            selectedCandidate.length === 0 ||
                            actionButtonsDisabled[0]
                          }
                        >
                          Advance
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={() => {
                            handleBulkActionIsDisqualify(true);
                            handleBulkActionModal(true);
                          }}
                          disabled={
                            !selectedCandidate ||
                            selectedCandidate.length === 0 ||
                            actionButtonsDisabled[1]
                          }
                        >
                          Disqualify
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={handleMediaAnswerDownload}
                          disabled={
                            candidates.length === 0 ||
                            !selectedCandidate ||
                            selectedCandidate.length === 0 ||
                            actionButtonsDisabled[2]
                          }
                        >
                          Download Video
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={handleAddAudioQuestion}
                          disabled={
                            candidates.length === 0 ||
                            !selectedCandidate ||
                            selectedCandidate.length === 0
                          }
                        >
                          Add Audio Questions
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={() => handleCustomEmailModal(true)}
                          disabled={
                            candidates.length === 0 ||
                            !selectedCandidate ||
                            selectedCandidate.length === 0
                          }
                        >
                          Send Custom Mail
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-white"
                          onClick={handleFlagCV}
                          disabled={
                            candidates.length === 0 ||
                            !selectedCandidate ||
                            selectedCandidate.length === 0
                          }
                        >
                          Flag CV
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded bg-white px-2 py-1 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                          onClick={() => setExportModalOpen(true)}
                        >
                          <ArrowDownTrayIcon className="h-4 w-4" />
                          Export CSV
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-start">
                      <span className="whitespace-nowrap">{s.name}</span>
                      {s.sort && (
                        <span
                          className={`cursor-pointer hover:bg-gray-200 rounded-full dark:hover:bg-entntorange py-0.5 px-1.5 flex items-center ${s.isActive && "bg-blue-100 dark:bg-entntblue"
                            }`}
                          onClick={() => {
                            handleSortChange(s);
                          }}
                        >
                          <span
                            className={
                              s.isActive ? "text-entntblue dark:text-gray-300" : ""
                            }
                          >
                            {s.asc ? (
                              <ArrowUpIcon height={15} />
                            ) : (
                              <ArrowDownIcon height={15} />
                            )}
                          </span>
                          <span
                            className={`${s.isActive && "text-entntblue dark:text-gray-300"
                              } text-xs font-medium`}
                          >
                            {s.isActive &&
                              sort.findIndex((st) => st.name === s.name) + 1}
                          </span>
                        </span>
                      )}
                      {i === 0 && (
                        <div className="flex space-x-3">
                          <Switch
                            checked={agebased}
                            onChange={() => setagebased(!agebased)}
                            className={`group relative ml-2 flex items-center h-4 w-8 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none bg-gray-200 border ${agebased ? "border-entntblue" : "border-entntorange"
                              }`}
                          >
                            <span
                              aria-hidden="true"
                              className={`pointer-events-none inline-block h-3 w-3 transform rounded-full shadow-lg transition duration-200 ease-in-out ${agebased
                                ? "translate-x-4 bg-entntblue"
                                : "translate-x-0.5 bg-entntorange"
                                }`}
                            />

                            <span
                              className={`absolute text-xs font-bold ${agebased
                                ? "text-entntblue left-1 "
                                : "text-entntorange right-2"
                                }`}
                            >
                              {agebased ? "A" : "E"}
                            </span>
                          </Switch>
                          <span
                            title="Refresh"
                            className="cursor-pointer hover:text-gray-600"
                            onClick={() => {
                              if (roleId && token)
                                refreshRoleCandidateCache(roleId, token).then(() => {
                                  window.location.reload();
                                });
                            }}
                          >
                            <ArrowPathIcon className="h-4" />
                          </span>
                        </div>
                      )}
                    </div>
                  </th>
                )
          )}

          {showRoleQGrading && (() => {
            // Same sort control as the generic columns, driven by the "QGrade" entry in
            // sortOptions, so clicking it sorts server-side by applicationScore.
            const s = sortOptions.find((o) => o.name === QGRADE_HEADER);
            return (
              <th
                scope="col"
                className="py-3 pr-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-300 whitespace-nowrap"
                title="Score from the candidate's answers to the role questions, weighted by the role's answer weights"
              >
                <div className="flex items-center justify-start">
                  <span className="whitespace-nowrap">QGrade</span>
                  {s?.sort && (
                    <span
                      className={`cursor-pointer hover:bg-gray-200 rounded-full dark:hover:bg-entntorange py-0.5 px-1.5 flex items-center ${s.isActive && "bg-blue-100 dark:bg-entntblue"}`}
                      onClick={() => handleSortChange(s)}
                    >
                      <span className={s.isActive ? "text-entntblue dark:text-gray-300" : ""}>
                        {s.asc ? <ArrowUpIcon height={15} /> : <ArrowDownIcon height={15} />}
                      </span>
                      <span className={`${s.isActive && "text-entntblue dark:text-gray-300"} text-xs font-medium`}>
                        {s.isActive && sort.findIndex((st) => st.name === s.name) + 1}
                      </span>
                    </span>
                  )}
                </div>
              </th>
            );
          })()}

          {roleListQuestions.map((q) => {
            const fullQuestion = stripHtml(q.text);
            const headerLabel =
              q.fieldName && q.fieldName.trim().length > 0
                ? q.fieldName.trim()
                : fullQuestion;
            return (
              <th
                key={`roleq-${q.id}`}
                scope="col"
                className="py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-300 w-40 max-w-40"
                title={q.fieldName && q.fieldName.trim().length > 0 ? headerLabel : fullQuestion}
              >
                <span className="block truncate">{headerLabel || "-"}</span>
              </th>
            );
          })}
        </tr>
      </thead>
    </>
  );
}
